/**
 * The KINDS of request the API client can build — one place for what the UI offers,
 * what a blank one looks like, and how a row is labelled.
 *
 * Until 19.09.2026 the WebSocket / SSE / gRPC builders could only be reached through a
 * collection import or the AI tool: no button created one. The "+" tab menu and the
 * sidebar's "Add request" entries both read from here, so the two can never offer
 * different lists.
 */
import { REQUEST_KINDS, normalizeRequestKind } from './workspaceTabPersistence';
import { httpMethodLabelClass } from './httpMethodLabelClass';

/**
 * What the "new request" picker offers, in order. `id` names the OPTION, `kind` is what
 * gets stored (`api_requests.type`). GraphQL is an option, not a kind: it is an HTTP
 * request in GraphQL body mode — `preset` is what picking that mode by hand would set.
 * `labelKey` → locales `requestKinds.*`; `badge` is what a sidebar row shows.
 */
export const REQUEST_KIND_OPTIONS = Object.freeze([
  { id: 'http', kind: 'http', labelKey: 'requestKinds.http', badge: 'HTTP' },
  {
    id: 'graphql', kind: 'http', labelKey: 'requestKinds.graphql', badge: 'GQL',
    preset: {
      name: 'New GraphQL query', method: 'POST', bodyType: 'graphql', editorTab: 'body',
      headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }, { key: '', value: '', enabled: true }],
    },
  },
  { id: 'websocket', kind: 'websocket', labelKey: 'requestKinds.websocket', badge: 'WS' },
  { id: 'sse', kind: 'sse', labelKey: 'requestKinds.sse', badge: 'SSE' },
  { id: 'grpc', kind: 'grpc', labelKey: 'requestKinds.grpc', badge: 'gRPC' },
]);

const optionById = (id) => REQUEST_KIND_OPTIONS.find((o) => o.id === id) || REQUEST_KIND_OPTIONS[0];

/** Which option's ICON stands for this request / tab: its kind — or GraphQL, by its body mode. */
export function requestOptionId(request) {
  const kind = normalizeRequestKind(request?.requestProtocol || request?.type);
  if (kind !== 'http') return kind;
  return (request?.bodyType || request?.body_type) === 'graphql' ? 'graphql' : 'http';
}

const BLANK_NAME = { http: 'New Request', websocket: 'New WebSocket', sse: 'New SSE stream', grpc: 'New gRPC call' };

/**
 * Anything → a kind. Click handlers hand their EVENT to `onClick={fn}`; an event, a
 * typo or nothing at all is plain HTTP, never a crash and never a fifth kind.
 */
export function asRequestKind(value) {
  if (typeof value !== 'string') return 'http';
  if (REQUEST_KINDS.includes(value)) return value;
  return optionById(value).kind; // an option id such as 'graphql' → the kind it is stored as
}

/** The unsaved request a new tab starts from. `extra` = where it belongs (collectionId, folderId). */
export function blankRequestOfKind(kind, extra = {}) {
  const resolved = asRequestKind(kind);
  const preset = typeof kind === 'string' ? optionById(kind).preset : null;
  return {
    name: BLANK_NAME[resolved],
    method: 'GET',
    url: '',
    headers: [{ key: '', value: '', enabled: true }],
    params: [{ key: '', value: '', enabled: true }],
    cookies: [{ key: '', value: '', enabled: true }],
    body: '',
    bodyType: 'none',
    collectionId: null,
    id: null, // no backend id yet — assigned when saved
    ...(preset ? { ...preset, headers: preset.headers.map((h) => ({ ...h })) } : {}),
    ...extra,
    type: resolved,
  };
}

/**
 * The kind to SAVE. The builders hand `saveRequest` the fields they edit — and `type` is
 * not one of them; it lives on the tab (new request) or on the saved row (existing one).
 * Without this the cloud payload fell back to 'http': a new WebSocket was stored as HTTP,
 * and every Ctrl+S on an existing one wrote 'http' over it.
 */
export function kindToSave(...sources) {
  const found = sources.map((s) => s?.type).find((type) => typeof type === 'string' && REQUEST_KINDS.includes(type));
  return found || 'http';
}

const KIND_BADGE_CLASS = { websocket: 'text-purple-700', sse: 'text-teal-700', grpc: 'text-blue-400' };

/**
 * What a request ROW shows before its name: the HTTP verb — or, for the other kinds,
 * the protocol. A saved WebSocket carries `method: 'GET'` (the column is not nullable),
 * so printing the method called it a GET.
 */
export function requestRowBadge(request) {
  const kind = normalizeRequestKind(request?.type);
  if (kind === 'http') {
    return { label: request?.method || 'GET', className: httpMethodLabelClass(request?.method) };
  }
  const option = optionById(kind);
  return { label: option.badge, className: KIND_BADGE_CLASS[kind] };
}
